import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Text } from '@quintoandar-tokko/jopijs';
import BaseModal from '../../BaseModal';
import {
  clearPerformanceModalFilters,
  setPerformanceModals,
} from '@/store/slices/components/dashboard/DashboardSlice';
import buildQuery from '@/components/Dashboard/utils/buildQuery';
import Trans from '@/components/shared/Trans';
import translations from '@/components/Dashboard/Dashboard.trans.json';
import DownloadButton from '@/components/Dashboard/DashboardTables/components/DownloadButton';
import parentStyles from '../../performanceModals.module.scss';
import specificStyles from './productionRateModal.module.scss';
import buildTableQuery from '@/components/Dashboard/utils/buildTableQuery';
import useAsyncTableExcelDownload from '@/components/Dashboard/customHooks/useAsyncTableExcelDownload';
import { TABLES } from '@/components/Dashboard/constants';
import TableSearchEmptyState from '@/components/Dashboard/DashboardTables/TableSearchEmptyState';
import SearchInput from '../../SearchInput';
import ProductionRateTable from '@/components/Dashboard/DashboardTables/ProductionRateTable';
import Checkbox from '@/components/checkbox/Checkbox';
import TableDropdown from '@/components/Dashboard/DashboardTables/components/TableDropdown';
import { productionRateStatusOptions } from '../utils';
import UseShowGroupByBranch from '@/components/Dashboard/utils/UseShowGroupByBranch';
import { AGENT } from '@/components/FilterBar/constants/filtersConstants';
import useTrackPerformanceTable from '@/components/Dashboard/customHooks/useTrackDashboard';
import { TRACK_ELEMENT } from '@/components/Dashboard/constants/track';
import { getPerformanceProductionRateTable } from '@/store/slices/components/dashboard/service';

export default function ProductionRateModal() {
  const dispatch = useDispatch();
  const { selectedIds, selectAgents, selectBranches } = useSelector(
    (state) => state.FilterBarSlice.filters
  );
  const { productionRate } = useSelector(
    (state) => state.DashboardSlice.performanceModals
  );
  const {
    production_rate,
    list,
    loading,
    sortBy,
    sortDirection,
    searchQuery,
    searchTypeFlag,
    searchStatus,
    groupByBranch,
  } = productionRate;

  const showGroupByBranch = UseShowGroupByBranch();
  const track = useTrackPerformanceTable();
  const { handleDownloadExcel: downloadExcel, isDownloading } =
    useAsyncTableExcelDownload(TABLES.PRODUCTION_RATE);
  const isHeaderReady = Boolean(
    document.querySelector('#production-rate-modal-header')
  );

  useEffect(() => {
    const fetchData = () => {
      const query = buildQuery(selectedIds);
      const tableQuery = buildTableQuery(productionRate);

      dispatch(getPerformanceProductionRateTable({ query, tableQuery }));
    };

    fetchData();
  }, [
    sortBy,
    sortDirection,
    searchQuery,
    searchTypeFlag,
    searchStatus,
    groupByBranch,
  ]);

  const handleDownloadExcel = () => {
    const query = buildQuery(selectedIds);
    const tableQuery = buildTableQuery(productionRate);
    downloadExcel(query, tableQuery);
    track.productionRate(TRACK_ELEMENT.DOWNLOAD_BUTTON);
  };

  const handleClearFilters = () => {
    const modal = 'productionRate';
    const filters = ['searchQuery', 'searchStatus', 'groupByBranch'];
    dispatch(clearPerformanceModalFilters({ modal, filters }));
  };

  const selectedStatusOption =
    productionRateStatusOptions.find(
      (option) => option.value === searchStatus
    ) ?? productionRateStatusOptions[1];

  const uniqueAgent =
    selectedIds.filter((x) => x.filter_type === AGENT).length === 1;

  const handleBranchGroupCheck = () => {
    dispatch(
      setPerformanceModals({
        key: 'productionRate',
        subkey: 'groupByBranch',
        value: !groupByBranch,
      })
    );
    track.productionRate(TRACK_ELEMENT.BRANCH_GROUP_CHECKBOX);
  };

  const handleAgentBranchChange = (option) => {
    dispatch(
      setPerformanceModals({
        key: 'productionRate',
        subkey: 'searchStatus',
        value: option.value,
      })
    );
    track.productionRate(TRACK_ELEMENT.DROPDOWN_SHOW_ALL);
  };

  return (
    <BaseModal
      id="production-rate-modal"
      title={`${Trans(translations, 'ProductionRate')}: ${
        production_rate?.rate ?? '0'
      }`}
      className={specificStyles.productionRateModal}
      handleClose={() =>
        dispatch(
          setPerformanceModals({
            key: 'productionRate',
            subkey: 'isOpen',
            value: false,
          })
        )
      }
      headerId="production-rate-modal-header"
    >
      <Box className={parentStyles.modalControls}>
        {!uniqueAgent && (
          <SearchInput
            modal="productionRate"
            maxWidth={selectBranches.length > 1 ? 520 : '100%'}
          />
        )}

        {showGroupByBranch && (
          <Box
            className={parentStyles.checkboxWrapper}
            onClick={handleBranchGroupCheck}
          >
            <Checkbox checked={groupByBranch} />
            <Box as="span" className={parentStyles.checkboxText}>
              {Trans(translations, 'GroupByBranch')}
            </Box>
          </Box>
        )}

        {(selectAgents.length > 1 || selectBranches.length > 1) &&
          !uniqueAgent && (
            <Box className={parentStyles.dropdownWrapper}>
              <Text variant={parentStyles.dropdownWrapperLabel}>
                {Trans(translations, 'NewContactsTable.Show')}
              </Text>
              <TableDropdown
                selectedOption={selectedStatusOption}
                options={productionRateStatusOptions}
                onSelect={(option) => handleAgentBranchChange(option)}
              />
            </Box>
          )}
        {!uniqueAgent && (
          <DownloadButton
            onClick={handleDownloadExcel}
            disabled={isDownloading}
          />
        )}
      </Box>
      {searchQuery && list?.length === 0 && !loading ? (
        <TableSearchEmptyState onClearFilters={handleClearFilters} />
      ) : (
        <BaseModal.TableContainer>
          <ProductionRateTable />
          {uniqueAgent &&
            isHeaderReady &&
            createPortal(
              <DownloadButton
                onClick={handleDownloadExcel}
                disabled={isDownloading}
              />,
              document.querySelector('#production-rate-modal-header')
            )}
        </BaseModal.TableContainer>
      )}
    </BaseModal>
  );
}
